// @flow

import React, { Component } from 'react';

import { translate } from '../../../base/i18n';
import { Icon, IconPopOut } from '../../../base/icons';
import { MEDIA_TYPE } from '../../../base/media';
import { connect } from '../../../base/redux';
import { Tooltip } from '../../../base/tooltip';
import { getTrackByMediaTypeAndParticipant } from '../../../base/tracks/functions';
import { showWarningNotification } from '../../../notifications/actions';
import { getPresentationWindow, getStreamFromTrack } from '../../index';

type Props = {

  /**
   * Invoked to dispatch Redux actions.
   */
  dispatch: Function,

  /**
   * The screenshare video track to render in the presentation window.
   */
  _screenshareTrack: Object | null,

  /**
   * Whether the local participant is currently screen sharing.
   */
  _isScreenSharing: boolean,

  /**
   * Invoked to obtain translated strings.
   */
  t: Function,
};

type State = {

  /**
   * Whether the presentation window is currently open.
   */
  isWindowOpen: boolean,
};

/**
 * Renders a clickable label on the LargeVideo that opens the screen share
 * in a separate presentation window.
 */
class PresentationWindowButton extends Component<Props, State> {
    /**
   * Initializes a new {@code PresentationWindowButton} instance.
   *
   * @param {Props} props - The read-only properties.
   */
    constructor(props: Props) {
        super(props);

        this.state = {
            isWindowOpen: false
        };

        this._onClick = this._onClick.bind(this);
        this._onWindowClosed = this._onWindowClosed.bind(this);
    }

    /**
   * Sets up the window close listener when the component mounts.
   *
   * @inheritdoc
   */
    componentDidMount() {
        getPresentationWindow().onWindowClosed(this._onWindowClosed);
    }

    /**
   * Removes the window close listener when the component unmounts.
   *
   * @inheritdoc
   */
    componentWillUnmount() {
        getPresentationWindow().off('close', this._onWindowClosed);
    }

    /**
   * Updates the window open state when the presentation window closes.
   *
   * @private
   * @returns {void}
   */
    _onWindowClosed() {
        this.setState({ isWindowOpen: false });
    }

    /**
   * Opens the presentation window with the screenshare track stream.
   *
   * @private
   * @returns {void}
   */
    _onClick() {
        const { _screenshareTrack, dispatch } = this.props;
        const stream = getStreamFromTrack(_screenshareTrack);

        if (!stream) {
            return;
        }

        const win = getPresentationWindow().open(stream, {
            title: 'Screen Share'
        });

        if (win) {
            this.setState({ isWindowOpen: true });
        } else if (dispatch) {
            dispatch(showWarningNotification({
                titleKey: 'notify.popupBlockedTitle',
                descriptionKey: 'notify.popupBlockedDescription'
            }));
        }
    }

    /**
   * Implements React's {@link Component#render}.
   *
   * @inheritdoc
   */
    render() {
        const { _isScreenSharing, float = false, t } = this.props;
        const { isWindowOpen } = this.state;

        if (!_isScreenSharing) {
            return null;
        }

        return (
            <Tooltip
                content = { t('toolbar.presentationWindow') }
                position = { 'left' }>
                <button
                    className = { `${float ? 'toolbox-icon' : 'label_buttons'} ${
                        isWindowOpen ? 'toggled' : ''
                    }` }
                    onClick = { this._onClick }>
                    <Icon
                        src = { IconPopOut }
                        width = { 20 } />
                </button>
            </Tooltip>
        );
    }
}

/**
 * Maps (parts of) the Redux state to the associated props.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state) {
    const tracks = state['features/base/tracks'];
    const { participantId } = state['features/large-video'];

    // Find the screenshare (desktop) track for the participant shown in large video
    const screenshareTrack
        = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, participantId)
            || tracks.find(t => t.videoType === 'desktop' && !t.muted);

    return {
        _screenshareTrack: screenshareTrack || null,
        _isScreenSharing: Boolean(screenshareTrack)
    };
}

export default connect(_mapStateToProps)(translate(PresentationWindowButton));
